import React from 'react';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Typography, Divider, Button, IconButton, FormGroup, Checkbox } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { WaField, WaCalendarIcon, WaLabel, optionalLabel } from '../whatsappPreview';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import { CalendarPickerCore } from '../../../../flow-core/components/CalendarPickerCore';

var WEEKDAYS = [
  { id: 'SUNDAY', label: 'Sunday' },
  { id: 'MONDAY', label: 'Monday' },
  { id: 'TUESDAY', label: 'Tuesday' },
  { id: 'WEDNESDAY', label: 'Wednesday' },
  { id: 'THURSDAY', label: 'Thursday' },
  { id: 'FRIDAY', label: 'Friday' },
  { id: 'SATURDAY', label: 'Saturday' },
];

export const CalendarPickerComponent = new FlowComponent({
  ...CalendarPickerCore,
  renderPreview(properties) {
    // Flow JSON gives a range calendar {"start-date", "end-date"} pairs; the builder keeps the
    // start's label in `label` and the end's in `end-label`.
    const half = (value, end) => (value && typeof value === 'object' ? value[end ? 'end-date' : 'start-date'] : value);
    const required = (end) => !!half(properties.required, end);
    if (properties.mode !== 'range') {
      return (
        <WaField
          icon={<WaCalendarIcon />}
          label={optionalLabel(properties.label || 'Select date', properties.required)}
          helper={properties['helper-text']}
          properties={properties}
        />
      );
    }
    return (
      <Box sx={{ opacity: properties.visible === false ? 0.5 : 1, mt: 2 }}>
        {properties.title && <WaLabel>{properties.title}</WaLabel>}
        {properties.description && (
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: -0.75 }}>{properties.description}</Typography>
        )}
        <WaField
          icon={<WaCalendarIcon />}
          label={optionalLabel(half(properties.label, false) || 'Start date', required(false))}
          helper={half(properties['helper-text'], false)}
          properties={properties}
        />
        <WaField
          icon={<WaCalendarIcon />}
          label={optionalLabel(properties['end-label'] || half(properties.label, true) || 'End date', required(true))}
          helper={half(properties['helper-text'], true)}
          properties={properties}
        />
      </Box>
    );
  },
  renderPropertyEditor(properties, onChange) {
    var handleAddUnavailableDate = () => {
      var currentDates = properties['unavailable-dates'] || [];
      var today = new Date().toISOString().split('T')[0];
      onChange('unavailable-dates', [...currentDates, today]);
    };

    var handleRemoveUnavailableDate = (index) => {
      var currentDates = properties['unavailable-dates'] || [];
      onChange('unavailable-dates', currentDates.filter((_, i) => i !== index));
    };

    var handleUnavailableDateChange = (index, value) => {
      var currentDates = properties['unavailable-dates'] || [];
      var newDates = [...currentDates];
      newDates[index] = value;
      onChange('unavailable-dates', newDates);
    };

    var handleIncludeDayToggle = (dayId) => {
      var currentDays = properties['include-days'] || [];
      if (currentDays.includes(dayId)) {
        onChange('include-days', currentDays.filter((d) => d !== dayId));
      } else {
        onChange('include-days', [...currentDays, dayId]);
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label="Field Name"
          value={properties.name || ''}
          onChange={(e) => onChange('name', e.target.value)}
          required
          size="small"
          helperText="Used to identify this field in the form data"
        />

        <TextField
          fullWidth
          label="Label"
          value={properties.label || ''}
          onChange={(e) => onChange('label', e.target.value)}
          required
          size="small"
          inputProps={{ maxLength: 40 }}
          helperText={`${(properties.label || '').length}/40 characters`}
        />

        <FormControl fullWidth size="small">
          <InputLabel>Mode</InputLabel>
          <Select
            value={properties.mode || 'single'}
            onChange={(e) => onChange('mode', e.target.value)}
            label="Mode"
          >
            <MenuItem value="single">Single Date</MenuItem>
            <MenuItem value="range">Date Range</MenuItem>
          </Select>
        </FormControl>

        {properties.mode === 'range' && (
          <>
            {/* WhatsApp labels the two ends separately; "Label" above is the start's. */}
            <TextField
              fullWidth
              label="End date label"
              value={properties['end-label'] || ''}
              onChange={(e) => onChange('end-label', e.target.value)}
              size="small"
              placeholder="End date"
              inputProps={{ maxLength: 40 }}
              helperText={`${(properties['end-label'] || '').length}/40 characters (range mode only)`}
            />

            <TextField
              fullWidth
              label="Title"
              value={properties.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
              size="small"
              inputProps={{ maxLength: 80 }}
              helperText={`${(properties.title || '').length}/80 characters (range mode only)`}
            />

            <TextField
              fullWidth
              label="Description"
              value={properties.description || ''}
              onChange={(e) => onChange('description', e.target.value)}
              size="small"
              multiline
              rows={2}
              inputProps={{ maxLength: 300 }}
              helperText={`${(properties.description || '').length}/300 characters (range mode only)`}
            />
          </>
        )}

        <TextField
          fullWidth
          label="Helper Text"
          value={properties['helper-text'] || ''}
          onChange={(e) => onChange('helper-text', e.target.value)}
          size="small"
        />

        <TextField
          fullWidth
          type="date"
          label="Initial Value"
          value={properties['init-value'] || ''}
          onChange={(e) => onChange('init-value', e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Date Range Constraints
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            type="date"
            label="Min Date"
            value={properties['min-date'] || ''}
            onChange={(e) => onChange('min-date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            fullWidth
            type="date"
            label="Max Date"
            value={properties['max-date'] || ''}
            onChange={(e) => onChange('max-date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box>

        {properties.mode === 'range' && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Min Days"
              type="number"
              value={properties['min-days'] || ''}
              onChange={(e) => onChange('min-days', e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              inputProps={{ min: 1 }}
              helperText="Range mode"
            />
            <TextField
              label="Max Days"
              type="number"
              value={properties['max-days'] || ''}
              onChange={(e) => onChange('max-days', e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              inputProps={{ min: 1 }}
              helperText="Range mode"
            />
          </Box>
        )}

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Available Days of Week
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Leave all unchecked to allow all days
        </Typography>

        <FormGroup row>
          {WEEKDAYS.map((day) => (
            <FormControlLabel
              key={day.id}
              control={
                <Checkbox
                  size="small"
                  checked={(properties['include-days'] || []).includes(day.id)}
                  onChange={() => handleIncludeDayToggle(day.id)}
                />
              }
              label={day.label.substring(0, 3)}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Unavailable Dates
        </Typography>

        <Box>
          {(properties['unavailable-dates'] || []).map((date, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}
            >
              <TextField
                type="date"
                size="small"
                value={date || ''}
                onChange={(e) => handleUnavailableDateChange(index, e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveUnavailableDate(index)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddUnavailableDate}
            variant="outlined"
          >
            Add Unavailable Date
          </Button>
        </Box>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Validation
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={properties.required || false}
              onChange={(e) => onChange('required', e.target.checked)}
            />
          }
          label="Required"
        />

        <TextField
          fullWidth
          label="Error Message"
          value={properties['error-message'] || ''}
          onChange={(e) => onChange('error-message', e.target.value)}
          size="small"
        />

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          State
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={properties.enabled !== false}
              onChange={(e) => onChange('enabled', e.target.checked)}
            />
          }
          label="Enabled"
        />

        <FormControlLabel
          control={
            <Switch
              checked={properties.visible !== false}
              onChange={(e) => onChange('visible', e.target.checked)}
            />
          }
          label="Visible"
        />
      </Box>
    );
  },
});
